/**
 * WAVE7-D — prawdziwe puzzle z obrazka (nie kolorowe emoji-klocki).
 * Generuje teksturę źródłową + cropy per klocek.
 */
import Phaser from 'phaser';
import type { GridSize } from './puzzleLogic.js';
import { pieceCount, posOf } from './puzzleLogic.js';

export const PUZZLE_SOURCE_KEY = 'puzzle_source_main';
export const PUZZLE_PREVIEW_KEY = 'puzzle_preview_main';

/** Id obrazków do wyboru (obecnie 1 proceduralny — gotowy na assety). */
export type PuzzlePictureId = 'park' | 'friends' | 'icecream';

export const PUZZLE_PICTURES: readonly {
  id: PuzzlePictureId;
  labelPl: string;
  emoji: string;
}[] = [
  { id: 'park', labelPl: 'Park', emoji: '🌳' },
  { id: 'friends', labelPl: 'Przyjaciele', emoji: '🐶' },
  { id: 'icecream', labelPl: 'Lody', emoji: '🍦' },
] as const;

export function pieceTextureKey(pictureId: PuzzlePictureId, pieceId: number): string {
  return `puzzle_piece_${pictureId}_${pieceId}`;
}

/**
 * Narysuj pastelowy obrazek Toca-styl (proceduralnie — zero emoji w klockach).
 */
export function ensurePuzzleSource(
  scene: Phaser.Scene,
  pictureId: PuzzlePictureId,
  sizePx = 512,
): string {
  const key = `${PUZZLE_SOURCE_KEY}_${pictureId}`;
  if (scene.textures.exists(key)) return key;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawPicture(g, pictureId, sizePx);
  g.generateTexture(key, sizePx, sizePx);
  g.destroy();
  return key;
}

function drawPicture(
  g: Phaser.GameObjects.Graphics,
  id: PuzzlePictureId,
  s: number,
): void {
  g.clear();
  // niebo
  g.fillGradientStyle(0xb3e5fc, 0xb3e5fc, 0xfff8e1, 0xfff8e1, 1);
  g.fillRect(0, 0, s, s);

  if (id === 'park') {
    // trawa
    g.fillStyle(0xa5d6a7, 1);
    g.fillRect(0, s * 0.55, s, s * 0.45);
    // słońce
    g.fillStyle(0xffe082, 1);
    g.fillCircle(s * 0.82, s * 0.18, s * 0.1);
    // drzewo
    g.fillStyle(0x8d6e63, 1);
    g.fillRect(s * 0.18, s * 0.4, s * 0.06, s * 0.28);
    g.fillStyle(0x81c784, 1);
    g.fillCircle(s * 0.21, s * 0.38, s * 0.14);
    // zjeżdżalnia (uproszczona)
    g.fillStyle(0x64b5f6, 1);
    g.fillTriangle(s * 0.45, s * 0.4, s * 0.7, s * 0.7, s * 0.48, s * 0.72);
    g.fillStyle(0xffcc80, 1);
    g.fillRoundedRect(s * 0.42, s * 0.38, s * 0.12, s * 0.2, 6);
    // kwiatki
    for (const [fx, fy, c] of [
      [0.55, 0.78, 0xf48fb1],
      [0.65, 0.82, 0xce93d8],
      [0.35, 0.8, 0xfff59d],
      [0.75, 0.76, 0x80deea],
    ] as const) {
      g.fillStyle(c, 1);
      g.fillCircle(s * fx, s * fy, s * 0.03);
    }
    // piesek
    g.fillStyle(0xfff8e1, 1);
    g.fillCircle(s * 0.78, s * 0.72, s * 0.07);
    g.fillStyle(0x4a3f55, 1);
    g.fillCircle(s * 0.76, s * 0.7, s * 0.012);
    g.fillCircle(s * 0.8, s * 0.7, s * 0.012);
    return;
  }

  if (id === 'friends') {
    g.fillStyle(0xffe0b2, 1);
    g.fillRect(0, s * 0.6, s, s * 0.4);
    // dwa pieski
    g.fillStyle(0xfff3e0, 1);
    g.fillCircle(s * 0.35, s * 0.55, s * 0.12);
    g.fillStyle(0xffccbc, 1);
    g.fillCircle(s * 0.62, s * 0.58, s * 0.11);
    // uszy
    g.fillStyle(0xffe0b2, 1);
    g.fillEllipse(s * 0.28, s * 0.48, s * 0.06, s * 0.08);
    g.fillEllipse(s * 0.42, s * 0.48, s * 0.06, s * 0.08);
    g.fillStyle(0xffab91, 1);
    g.fillEllipse(s * 0.55, s * 0.5, s * 0.05, s * 0.07);
    g.fillEllipse(s * 0.69, s * 0.5, s * 0.05, s * 0.07);
    // serce
    g.fillStyle(0xef9a9a, 1);
    g.fillCircle(s * 0.48, s * 0.32, s * 0.05);
    g.fillCircle(s * 0.55, s * 0.32, s * 0.05);
    g.fillTriangle(s * 0.43, s * 0.34, s * 0.6, s * 0.34, s * 0.515, s * 0.48);
    // kotek
    g.fillStyle(0xfff8e1, 1);
    g.fillCircle(s * 0.8, s * 0.72, s * 0.08);
    g.fillTriangle(s * 0.74, s * 0.66, s * 0.78, s * 0.58, s * 0.8, s * 0.66);
    g.fillTriangle(s * 0.8, s * 0.66, s * 0.82, s * 0.58, s * 0.86, s * 0.66);
    return;
  }

  // icecream
  g.fillStyle(0xfff3e0, 1);
  g.fillRect(0, s * 0.55, s, s * 0.45);
  // stoisko
  g.fillStyle(0xffab91, 1);
  g.fillRoundedRect(s * 0.15, s * 0.45, s * 0.7, s * 0.35, 12);
  g.fillStyle(0xffffff, 0.9);
  g.fillRoundedRect(s * 0.2, s * 0.5, s * 0.6, s * 0.12, 8);
  // rożek
  g.fillStyle(0xffcc80, 1);
  g.fillTriangle(s * 0.45, s * 0.35, s * 0.55, s * 0.35, s * 0.5, s * 0.55);
  // gałki
  g.fillStyle(0xf48fb1, 1);
  g.fillCircle(s * 0.48, s * 0.3, s * 0.055);
  g.fillStyle(0xa5d6a7, 1);
  g.fillCircle(s * 0.54, s * 0.28, s * 0.05);
  g.fillStyle(0x81d4fa, 1);
  g.fillCircle(s * 0.45, s * 0.25, s * 0.045);
  // chmurki
  g.fillStyle(0xffffff, 0.95);
  g.fillCircle(s * 0.2, s * 0.18, s * 0.06);
  g.fillCircle(s * 0.26, s * 0.18, s * 0.07);
  g.fillCircle(s * 0.32, s * 0.18, s * 0.06);
}

/**
 * Wytnij klocki z obrazka źródłowego → osobne tekstury.
 */
export function ensurePuzzlePieces(
  scene: Phaser.Scene,
  pictureId: PuzzlePictureId,
  size: GridSize,
  sourceKey: string,
): void {
  const n = pieceCount(size);
  const tex = scene.textures.get(sourceKey);
  const src = tex.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const sw = (src as HTMLImageElement).width || 512;
  const sh = (src as HTMLImageElement).height || 512;
  const cellW = Math.floor(sw / size);
  const cellH = Math.floor(sh / size);

  for (let id = 0; id < n; id++) {
    const key = pieceTextureKey(pictureId, id);
    if (scene.textures.exists(key)) {
      // przy zmianie size stare cropy mogą mieć zły rozmiar — nadpisz
      scene.textures.remove(key);
    }
    const { col, row } = posOf(id, size);
    const canvas = scene.textures.createCanvas(key, cellW, cellH);
    if (!canvas) continue;
    const ctx = canvas.getContext();
    ctx.drawImage(
      src as CanvasImageSource,
      col * cellW,
      row * cellH,
      cellW,
      cellH,
      0,
      0,
      cellW,
      cellH,
    );
    canvas.refresh();
  }
}
